import { carouselHeroMessages } from './messages/carousel-hero.messages';

import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import {
	AnchorPageRelayQuery,
	shapeAnchorContentfulResponse,
} from 'src/contentful/graphql/anchor-page';
import { anchorPageRelayQuery } from '@/__generated_contentful__/anchorPageRelayQuery.graphql';
import { CarouselProps } from '@/components/confluence/carousel/src/carousel';
import { useLogMeInD0to6 } from '@/components/confluence/hooks/use-log-me-in-d0to6.tsx';
import HeroSection from '@/templates/software/confluence/confluence-software-page/src/sections/log-me-in/hero';
import {
	AnchorContentsResponse,
	PaidLandingPersonaTypes,
} from '@/utils/anchor-queries/get-anchor-contents';
import { useExperimentExposure } from '@/utils/experiment/use-experiment-exposure';
import { experiments } from '@/utils/experiment/manifest';

import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';
import { ConfluenceSoftwarePageProps } from './confluence-software-page';
import { ConfluencePagePaidLpConflAnchor } from './paid-lp-confl-anchor';
import { isTargetedSubAnchor } from './subAnchors/subAnchor-util.tsx';

export interface ConfluenceSoftwarePageAnchorDesignRelayProps extends ConfluenceSoftwarePageProps {
	anchorContents?: AnchorContentsResponse | null;
	persona?: string;
	queryRef?: PreloadedQuery<anchorPageRelayQuery>;
}
export const ConfluenceSoftwarePageAnchorDesign = (
	pageProps: ConfluenceSoftwarePageAnchorDesignRelayProps,
) => {
	const intl = useIntl();
	const [exposureSubAnchorFired, fireSubAnchorExposure] = useExperimentExposure(
		experiments.cfind4782LandingPageSubAnchorV3,
	);
	useEffect(() => {
		if (isTargetedSubAnchor(pageProps.subAnchor) && !exposureSubAnchorFired) {
			fireSubAnchorExposure();
		}
	}, [exposureSubAnchorFired, fireSubAnchorExposure]);

	const data = usePreloadedQuery(
		AnchorPageRelayQuery,
		pageProps.queryRef || (null as unknown as PreloadedQuery<anchorPageRelayQuery>),
	);
	pageProps = Object.assign({}, pageProps, {
		anchorContents: shapeAnchorContentfulResponse(data),
	});

	const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeInD0to6({
		availableProducts: pageProps.availableProducts,
		userProfile: pageProps.userProfile,
	});

	const carouselSlides = [
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Design.dynamic-documentation.title',
				defaultMessage: 'Dynamic documentation',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Design.dynamic-documentation.body',
				defaultMessage:
					'Brainstorm ideas, document design guidelines, and take notes—all in one place.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1e81unagi717g13fj44qtgpk0h8r0537.webp',
			imgAltText: 'A Confluence page being edited',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Design.same-page.title',
				defaultMessage: 'Get everyone on the same page',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Design.same-page.body',
				defaultMessage:
					'Collaborate with cross-functional teams using real-time editing and inline comments.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/i0011ky0775062fk18784clr4dcxd5wg.webp',
			imgAltText: 'A Confluence page with multiple teammates editing and leaving comments',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Design.context-designers-need.title',
				defaultMessage: 'The context designers need',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.Design.context-designers-need.body',
				defaultMessage:
					'Stay organized with dedicated team workspaces, all equipped with AI-powered search.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/fpd20037c363n21cfmh45nc5m50co161.webp',
			imgAltText: 'Organized workspace in Confluence',
		},
	];

	const carouselHeroProps: CarouselProps = {
		'aria-label': intl.formatMessage(carouselHeroMessages.ariaLabel),
		id: 'confluence-design-carousel',
		slides: carouselSlides,
	};

	const pageId = 'anchor-lp-conf-team-design';

	let customHeroSection = null;
	if (shouldShowLogMeIn) {
		customHeroSection = (
			<FeatureExposed experimentKey="cfind-2801-get-paid-users-back-to-product" group="Test">
				<HeroSection
					confluenceSites={recentSites}
					userProfileName={userProfileName}
					hideExploreMore
					loggedInPaid
				/>
			</FeatureExposed>
		);
	}

	return (
		<ConfluencePagePaidLpConflAnchor
			{...pageProps}
			pageId={pageId}
			persona={'unknown' as PaidLandingPersonaTypes}
			pageVariant="design"
			dataProductKey="confluence-AnchorLpConfDesign"
			carouselHeroProps={carouselHeroProps}
			keyword={pageProps.keyword}
			customHeroSection={customHeroSection}
		/>
	);
};
